import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useToast } from "../components/Toast.js";
import type { TenantCustomerRoleType } from "../lib/customers.js";

// ADR-0154 §6.3 C7 — the hub's rule is BOTH halves: the character class AND a
// 64-character ceiling (`repos/customerRoleTypes.ts`). This panel checked only
// the pattern, so an over-long slug passed here and was refused by the server.
// Parity-gated in `hubVocabParity.test.ts`.
const SLUG_RE = /^[a-z0-9_]+$/;
const SLUG_MAX_CHARS = 64;

interface Props {
  onClose: () => void;
}

/**
 * ADR-0075 §3.4 — Role types admin (modal).
 *
 * Tenant-defined role names referenced by tenant_customer_users mappings
 * and watch RASCI configs. Slug renames are always allowed (UUID is the
 * stable reference key). Empty state is empty — no auto-seed.
 */
export function CustomerRoleTypesPanel({ onClose }: Props) {
  const toast = useToast();
  const [roleTypes, setRoleTypes] = useState<TenantCustomerRoleType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newLabel, setNewLabel] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newError, setNewError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ role_types: TenantCustomerRoleType[] }>(
        "/api/tenant/admin/customer-role-types?include_disabled=true"
      );
      setRoleTypes(res.role_types);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function createRole() {
    setNewError(null);
    if (newLabel.trim() === "") {
      setNewError("Label is required.");
      return;
    }
    if (!SLUG_RE.test(newSlug.trim()) || newSlug.trim().length > SLUG_MAX_CHARS) {
      setNewError("Slug must match ^[a-z0-9_]+$");
      return;
    }
    setBusy(true);
    try {
      await apiFetch("/api/tenant/admin/customer-role-types", {
        method: "POST",
        body: { label: newLabel.trim(), slug: newSlug.trim() }
      });
      setNewLabel("");
      setNewSlug("");
      await load();
    } catch (e) {
      setNewError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function patchRole(rt: TenantCustomerRoleType, patch: Partial<TenantCustomerRoleType>) {
    try {
      await apiFetch(`/api/tenant/admin/customer-role-types/${rt.id}`, {
        method: "PATCH",
        body: patch
      });
      await load();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    }
  }

  async function deleteRole(rt: TenantCustomerRoleType) {
    try {
      await apiFetch(`/api/tenant/admin/customer-role-types/${rt.id}`, { method: "DELETE" });
      await load();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    }
  }

  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal modalScrollable"
        role="dialog"
        aria-modal="true"
        aria-label="Manage customer role types"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, width: "90vw" }}
      >
        <div style={{ padding: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 12 }}>Customer role types</div>
          <div className="muted" style={{ marginBottom: 12 }}>
            Tenant-defined names for the relationship roles a user can hold on a customer (e.g. AE,
            CSM, AR). Watches reference these by ID, so renaming the slug is safe.
          </div>

          {error && <div className="help" style={{ color: "var(--danger-text)" }}>{error}</div>}
          {loading && <div className="muted">Loading…</div>}

          {!loading && (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Slug</th>
                    <th style={{ width: 100 }}>Sort</th>
                    <th style={{ width: 90 }}>Disabled?</th>
                    <th style={{ width: 100 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {roleTypes.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted" style={{ padding: 16, textAlign: "center" }}>
                        No role types yet. Add one to start mapping users to customers.
                      </td>
                    </tr>
                  )}
                  {roleTypes.map((rt) => (
                    <tr key={rt.id}>
                      <td>
                        <input
                          className="input"
                          defaultValue={rt.label}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== rt.label) void patchRole(rt, { label: v });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="input mono"
                          defaultValue={rt.slug}
                          onBlur={(e) => {
                            const v = e.target.value.trim();
                            if (v !== rt.slug) void patchRole(rt, { slug: v });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          defaultValue={rt.sort_order}
                          onBlur={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!Number.isNaN(v) && v !== rt.sort_order) {
                              void patchRole(rt, { sort_order: v });
                            }
                          }}
                        />
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={rt.is_disabled}
                          onChange={(e) => void patchRole(rt, { is_disabled: e.target.checked })}
                        />
                      </td>
                      <td>
                        <button
                          className="adminListAction isDanger"
                          onClick={() => void deleteRole(rt)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td>
                      <input
                        className="input"
                        placeholder="Account Executive"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        className="input mono"
                        placeholder="ae"
                        value={newSlug}
                        onChange={(e) => setNewSlug(e.target.value.toLowerCase())}
                      />
                    </td>
                    <td colSpan={2}></td>
                    <td>
                      <button
                        className="btn btnPrimary"
                        disabled={busy}
                        onClick={() => void createRole()}
                      >
                        Add
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
              {newError && (
                <div className="help" style={{ color: "var(--danger-text)", marginTop: 8 }}>
                  {newError}
                </div>
              )}
            </div>
          )}

          <div className="actions" style={{ justifyContent: "flex-end", marginTop: 16 }}>
            <button className="btn btnPrimary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
